import { CommonModule } from '@angular/common'
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Router, RouterLink } from '@angular/router'
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SvgIconComponent } from 'angular-svg-icon'
import { NzFormDirective } from 'ng-zorro-antd/form'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { Observable, Subscription, tap } from 'rxjs'

import { TableBaseAbstract } from '../../abstracts/table-base.abstract'
import { ButtonComponent } from '../../components/button/button.component'
import { FormlySelectFieldConfig } from '../../components/formly/types/select-field-type/select-field-type.component'
import { MasterAddressesComponent } from '../../components/master-addresses/master-addresses.component'
import { PageHeadlineComponent } from '../../components/page-headline/page-headline.component'
import { TableDataBase } from '../../components/table/table.component'
import { TypedTemplateDirective } from '../../directives/typed-template.directive'
import { Address } from '../../models/address.model'
import {
  FUNDING_CODES,
  FundingCode,
  MUNICIPALITIES,
  Municipality,
} from '../../models/data.model'
import { Expression } from '../../repositories/base.repository'
import { RecordsRepository } from '../../repositories/records/records.repository'
import { SnapshotRepository } from '../../repositories/snapshot/snaphot.repository'
import {
  FormlyTranslationService,
  provideFormlyTranslation,
} from '../../services/formly-translation.service'

export type TableData = TableDataBase & Address
export type FilterFormModel = {
  municipalities?: Municipality[]
  fundingCodes?: FundingCode[]
}
export type DialogFormModel = {
  name?: string
  note?: string
}

@Component({
  selector: 'app-address-master',
  standalone: true,
  providers: [provideFormlyTranslation('pages.addressMaster')],
  imports: [
    CommonModule,
    FormsModule,
    PageHeadlineComponent,
    TranslatePipe,
    FormlyModule,
    NzFormDirective,
    ReactiveFormsModule,
    ButtonComponent,
    SvgIconComponent,
    MasterAddressesComponent,
    TypedTemplateDirective,
    RouterLink,
  ],
  templateUrl: './address-master.component.html',
})
export class AddressMasterComponent
  extends TableBaseAbstract
  implements OnInit, OnDestroy
{
  @ViewChild('snapshotDialog', { static: true })
  private readonly snapshotDialog!: ElementRef<HTMLDialogElement>

  public override tableData: TableData[] = []

  public typeToken!: { item: string; currentRow: TableData }

  private subscription!: Subscription

  public filterForm = new FormGroup({})
  public filterModel: FilterFormModel = {}
  public filterFields: FormlyFieldConfig[] = []

  public snapshotDialogLoading: boolean = false
  public dialogForm = new FormGroup({})
  public dialogModel: DialogFormModel = {}
  public dialogFields: FormlyFieldConfig[] = []

  public constructor(
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly recordsRepository: RecordsRepository,
    private readonly snapshotRepository: SnapshotRepository,
    private readonly notificationService: NzNotificationService,
    private readonly formlyTranslationService: FormlyTranslationService
  ) {
    super()
    this.activeSorting = [
      {
        key: 'updatedAt',
        value: 'descend',
      },
    ]
  }

  public ngOnInit(): void {
    this.subscription = this.fetchTableData().subscribe()
    this.buildFilterForm()
    this.buildDialogForm()
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  private buildFilterForm(): void {
    this.filterFields = [
      {
        fieldGroupClassName:
          'grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-6',
        fieldGroup: [this.buildCountiesField(), this.buildFundingCodesField()],
      },
    ]
  }

  private buildCountiesField(): FormlySelectFieldConfig {
    const base = this.fieldBaseSetup('municipalities')
    return {
      ...base,
      type: 'select',
      props: {
        ...base.props,
        options: MUNICIPALITIES.map((county) => ({
          label: county,
          value: county,
        })),
      },
    }
  }

  private buildFundingCodesField(): FormlySelectFieldConfig {
    const base = this.fieldBaseSetup('fundingCodes')
    return {
      ...base,
      type: 'select',
      props: {
        ...base.props,
        options: this.formlyTranslationService.getTranslatedOptions(
          FUNDING_CODES,
          'fundingCode',
          true
        ),
      },
    }
  }

  private fieldBaseSetup(key: string): FormlyFieldConfig {
    return {
      key: key,
      props: {
        mode: 'multiple',
        label: `pages.addressMaster.filters.${key}.label`,
        placeholder: `pages.addressMaster.filters.${key}.placeholder`,
      },
    }
  }

  private buildDialogForm(): void {
    this.dialogFields = [this.buildNameField(), this.buildNoteField()]
  }

  private buildNameField(): FormlyFieldConfig {
    return {
      key: 'name',
      type: 'input',
      props: {
        label: 'pages.addressMaster.snapshotCreation.name.label',
        placeholder: 'pages.addressMaster.snapshotCreation.name.placeholder',
        required: true,
        pattern: /^.{1,50}$/,
      },
      validation: {
        messages: {
          required: 'pages.addressMaster.snapshotCreation.name.requiredError',
          pattern: 'pages.addressMaster.snapshotCreation.name.patternError',
        },
      },
    }
  }

  private buildNoteField(): FormlyFieldConfig {
    return {
      key: 'note',
      type: 'input',
      props: {
        label: 'pages.addressMaster.snapshotCreation.note.label',
        placeholder: 'pages.addressMaster.snapshotCreation.note.placeholder',
        required: true,
        pattern: /^.{1,250}$/,
      },
      validation: {
        messages: {
          required: 'pages.addressMaster.snapshotCreation.note.requiredError',
          pattern: 'pages.addressMaster.snapshotCreation.note.patternError',
        },
      },
    }
  }

  public onDialog(ev: MouseEvent): void {
    if (ev.target === this.snapshotDialog.nativeElement) {
      this.stopSnapshotCreation()
    }
  }

  public stopSnapshotCreation(): void {
    this.snapshotDialog.nativeElement.close()
    this.dialogForm.reset()
  }

  public createSnapshot(): void {
    if (this.dialogForm.valid) {
      this.snapshotDialogLoading = true
      this.snapshotRepository
        .create({
          ...this.dialogModel,
          filter: this.getFilter() as Expression[],
        })
        .subscribe({
          next: () => {
            this.notificationService.success(
              this.translateService.instant(
                'pages.addressMaster.snapshotCreation.notification.success'
              ),
              ''
            )
            this.snapshotDialogLoading = false
            this.stopSnapshotCreation()
            this.router.navigate(['snapshots'])
          },
          error: () => {
            this.notificationService.error(
              this.translateService.instant(
                'pages.addressMaster.snapshotCreation.notification.failure'
              ),
              ''
            )
            this.snapshotDialogLoading = false
          },
        })
    }
  }

  public onFilterChange(): void {
    this.pageIndex = 1
    this.updateTableData()
  }

  protected updateTableData(): void {
    this.isLoading = true
    this.subscription = this.fetchTableData().subscribe()
  }

  private fetchTableData(): Observable<unknown> {
    return this.recordsRepository
      .list({
        limit: this.pageSize,
        page: this.pageIndex,
        filter: this.getFilter(),
        sorting: this.getSorting(),
      })
      .pipe(
        tap((response) => {
          this.total = response.meta.totalCount
          this.tableData = response.data.map(
            (address) =>
              ({
                id: address.id,
                ...address.attributes,
              }) as TableData
          )
          this.isLoading = false
        })
      )
  }

  protected getFilter(): Expression<Address>[] {
    const filter: Expression<Address>[] = []

    if (this.sword) {
      filter.push(
        this.recordsRepository.getSearchQuery(this.sword, [
          'county',
          'place',
          'plz',
          'streetName',
          'houseNumber',
          'houseNumberAddition',
          'marking',
        ])
      )
    }

    const formFilters = this.filterModel
    const municipalities = formFilters.municipalities
    if (municipalities?.length) {
      if (municipalities.length === 1) {
        filter.push({
          op: 'eq',
          name: 'municipality',
          val: municipalities[0],
        })
      } else {
        filter.push({
          or: municipalities.map((county) => ({
            op: 'eq',
            name: 'municipality',
            val: county,
          })),
        })
      }
    }
    const fundingCodes = formFilters.fundingCodes
    if (fundingCodes?.length) {
      if (fundingCodes.length === 1) {
        filter.push({
          op: 'eq',
          name: 'referenceNumberForFundingProgram',
          val: fundingCodes[0],
        })
      } else {
        filter.push({
          or: fundingCodes.map((county) => ({
            op: 'eq',
            name: 'referenceNumberForFundingProgram',
            val: county,
          })),
        })
      }
    }

    return filter
  }
}
